import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakerWaveIcon, PlayIcon, StopIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import type { IBgm } from '@packages/global';
import MusicSelector from '../../common/MusicSelector';
import { episodeScriptApi } from '../../../services/user';

interface BgmCardProps {
  bgmUrl?: string;
  scriptId: string;
  playingAudio: string | null;
  onPlayAudio: (url: string, id: string) => void;
  onUpdate?: () => void;
}

export default function BgmCard({ bgmUrl, scriptId, playingAudio, onPlayAudio, onUpdate }: BgmCardProps) {
  const { t } = useTranslation(['script', 'common']);
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [updating, setUpdating] = useState(false);
  const isPlaying = playingAudio === 'bgm';

  const handleSelectBgm = async (bgm: IBgm) => {
    if (!bgm.audioUrl) {
      alert(t('script:bgm.noAudioFile'));
      return;
    }

    try {
      setUpdating(true);
      await episodeScriptApi.updateBgm(scriptId, bgm.audioUrl);
      setShowMusicSelector(false);
      onUpdate?.();
    } catch (error: any) {
      console.error('Failed to update BGM:', error);
      alert(error.message || t('script:bgm.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  if (!bgmUrl) return null;

  return (
    <>
      <div className="bg-bg-surface rounded-lg overflow-hidden group">
        {/* 正方形图片区域 - 用音乐图标代替 */}
        <div className="relative w-full aspect-square bg-gradient-to-br from-green-900/60 to-emerald-900/60 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <MusicalNoteIcon className="w-8 h-8 text-green-400" />
            </div>
            <span className="text-xs text-green-300/70">{t('script:bgm.title')}</span>
          </div>
        </div>

        <div className="p-3 space-y-2">
          <div className="text-sm font-semibold text-gray-50">{t('script:bgm.title')}</div>
          <div className="text-xs text-gray-400 truncate">{t('script:bgm.subtitle')}</div>
          {/* 操作按钮 - 与 NarratorCard 一致 */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPlayAudio(bgmUrl, 'bgm')}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors"
              title={isPlaying ? t('script:bgm.stopPlay') : t('script:bgm.playPreview')}
            >
              {isPlaying
                ? <StopIcon className="w-3.5 h-3.5 text-green-400" />
                : <PlayIcon className="w-3.5 h-3.5 text-gray-300" />}
            </button>
            <button
              onClick={() => setShowMusicSelector(true)}
              disabled={updating}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50"
              title={t('script:bgm.changeMusic')}
            >
              <MusicalNoteIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      <MusicSelector
        isOpen={showMusicSelector}
        onClose={() => setShowMusicSelector(false)}
        onSelect={handleSelectBgm}
      />
    </>
  );
}
